import React from 'react';
import {
  TextField,
  Typography,
  DropdownField,
  MenuItem,
} from '@one-thd/sui-atomic-components';
import {
  CityStateProps,
  ContactDetailsFormProps,
  RequiredFieldProps,
} from '@src/models/lorp';
import {
  LORP_ADDRESS_REQUIRED_MESSAGE,
  LORP_VERIFY_PICKUP_ADDRESS_MESSAGE,
  LORP_ZIP_REQUIRED_MESSAGE,
} from '@src/constants/analytics';

interface LORPContactFormProps {
  tempContactDetails?: ContactDetailsFormProps;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCityStateChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  handleInputBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  requiredFieldsStatus: RequiredFieldProps;
  isSTH?: boolean;
  availableCityStates?: CityStateProps[];
  unconfirmedAddress: boolean;
}
// Address fields should not be editable if fulfillment type is STH, this includes first/last name

export const LORPContactForm = ({
  tempContactDetails,
  requiredFieldsStatus,
  isSTH,
  handleInputChange,
  handleCityStateChange,
  handleSubmit,
  handleInputBlur,
  availableCityStates,
  unconfirmedAddress,
}: LORPContactFormProps) => {
  const {
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    addressLine5,
    city,
    state,
    zipCode,
  } = tempContactDetails || {};

  // Check if multiple cities are available
  const isMultipleCities =
    availableCityStates && availableCityStates?.length > 1;

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <div className="sui-mb-6">
        <TextField
          disabled={isSTH}
          fullWidth
          required
          id="first-name"
          placeholder="First Name"
          name="firstName"
          label="First Name"
          value={firstName}
          onChange={handleInputChange}
          statusMessage={
            !requiredFieldsStatus.firstName && 'Please provide your first name.'
          }
          status={!requiredFieldsStatus.firstName && 'error'}
          onBlur={handleInputBlur}
        />
      </div>
      <div className="sui-mb-6">
        <TextField
          disabled={isSTH}
          fullWidth
          required
          id="last-name"
          placeholder="Last Name"
          name="lastName"
          label="Last Name"
          value={lastName}
          onChange={handleInputChange}
          statusMessage={
            !requiredFieldsStatus.lastName && 'Please provide your last name.'
          }
          status={!requiredFieldsStatus.lastName && 'error'}
          onBlur={handleInputBlur}
        />
      </div>
      <div className="sui-mb-6">
        <TextField
          disabled={isSTH}
          fullWidth
          required
          id="pickup-address1"
          placeholder="Pickup Address"
          name="addressLine1"
          label="Pickup Address"
          value={addressLine1}
          onChange={handleInputChange}
          data-testid="pickup-address1"
          statusMessage={
            (!requiredFieldsStatus.addressLine1 &&
              LORP_ADDRESS_REQUIRED_MESSAGE) ||
            (unconfirmedAddress && LORP_VERIFY_PICKUP_ADDRESS_MESSAGE)
          }
          status={
            (!requiredFieldsStatus.addressLine1 && 'error') ||
            (unconfirmedAddress && 'warning')
          }
          onBlur={handleInputBlur}
        />
      </div>
      {/* Dont display pickup address 2, 3, 4, or 5 if locked and empty */}
      {/* Dont display address 3,4, or 5 if unlocked and empty */}
      {/* address 2 should always be available if its a BOSS order, regardless if its empty or not */}
      {!(isSTH && !addressLine2) && (
        <div className="sui-mb-6">
          <TextField
            fullWidth
            disabled={isSTH}
            optional
            id="pickup-address2"
            placeholder="Apartment, suite, building, etc."
            name="addressLine2"
            label="Pickup Address"
            value={addressLine2}
            onChange={handleInputChange}
            status={unconfirmedAddress && 'warning'}
            onBlur={handleInputBlur}
          />
        </div>
      )}
      {/* Only display additonal address lines if locked and are available */}
      {addressLine3 && (
        <div className="sui-mb-6">
          <TextField
            fullWidth
            disabled={isSTH}
            optional
            id="pickup-address3"
            name="addressLine3"
            label="Pickup Address"
            value={addressLine3}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
        </div>
      )}
      {addressLine4 && (
        <div className="sui-mb-6">
          <TextField
            fullWidth
            disabled={isSTH}
            optional
            id="pickup-address4"
            name="addressLine4"
            label="Pickup Address"
            value={addressLine4}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
        </div>
      )}
      {addressLine5 && (
        <div className="sui-mb-6">
          <TextField
            fullWidth
            disabled={isSTH}
            optional
            id="pickup-address5"
            name="addressLine5"
            label="Pickup Address"
            value={addressLine5}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
        </div>
      )}

      <div className="sui-flex sui-gap-[24px]">
        <Typography className="sui-text-gray-600">
          <TextField
            disabled={isSTH}
            required
            id="zip-code"
            placeholder="Zip Code"
            name="zipCode"
            label="ZIP Code"
            value={zipCode}
            onChange={handleInputChange}
            statusMessage={
              requiredFieldsStatus.zipCode
                ? 'Enter ZIP Code to see City and State'
                : LORP_ZIP_REQUIRED_MESSAGE
            }
            status={!requiredFieldsStatus.zipCode && 'error'}
          />
        </Typography>

        <div>
          {availableCityStates && (
            <>
              {isMultipleCities && (
                <DropdownField
                  id="city-state-dropdown"
                  value={city || state}
                  onChange={handleCityStateChange}
                  label="City, State *"
                >
                  {availableCityStates.map((cityState) => (
                    <MenuItem key={cityState.city} value={cityState.city}>
                      {`${cityState.city}, ${cityState.stateCode}`}
                    </MenuItem>
                  ))}
                  {!city && <MenuItem value={state}>{state}</MenuItem>}
                </DropdownField>
              )}

              {!isMultipleCities && (
                <div>
                  <Typography className="sui-mb-2">City, State *</Typography>
                  <Typography weight="bold">
                    {availableCityStates?.[0] &&
                      `${availableCityStates?.[0].city}, ${availableCityStates?.[0].stateCode}`}
                  </Typography>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* This hidden button allows the onSubmit callback to get called when user presses the enter key within a field since actual submission button is not within this form object */}
      <button
        type="submit"
        aria-hidden="true"
        aria-label="Submit contact details form"
      />
    </form>
  );
};
